/**
 * Textes explicatifs pour l'abattement des actes (taxAllowanceExplication)
 */
export const TAX_ALLOWANCE_MESSAGES = {
    /**
     * Taux à 100% car cotable à taux plein dans le cadre d'un forfait (perfusion ou pansement lourd ou BPCO ou prise de sang).
     */
    T100_FORFAIT_ACTE: "Taux à 100% car cotable à taux plein dans le cadre d'un forfait (perfusion ou pansement lourd ou BPCO ou prise de sang).",
    /**
     * Taux à 100% car les forfaits journaliers infirmiers sont toujours facturés à taux plein.
     */
    T100_FORFAIT: "Taux à 100% car les forfaits journaliers infirmiers sont toujours facturés à taux plein.",
    /**
     * Taux à 100% car 1er acte (Article 11B NGAP)
     */
    T100: "Taux à 100% car 1er acte (Article 11B NGAP)",
    /**
     * Taux à 50% car 2e acte après exception à taux plein.
     */
    T50_EXCEPTION: "Taux à 50% car 2e acte après exception à taux plein.",
    /**
     * Taux à 50% car 2nd acte (Article 11B NGAP)
     */
    T50: "Taux à 50% car 2nd acte (Article 11B NGAP)",
    /**
     * Taux à 0% car 3e acte et plus (Article 11B NGAP)
     */
    T0: "Taux à 0% car 3e acte et plus (Article 11B NGAP)",
    /**
     * Taux à 0% car un forfait journalier infirmier à déjà été facturé aujourd'hui pour ce patient.
     */
    T0_FORFAIT: "Taux à 0% car un forfait journalier infirmier à déjà été facturé aujourd'hui pour ce patient.",
    /**
     * Taux à 100% car exclu de la décote (Article 11B NGAP).
     */
    T100_EXCLU: "Taux à 100% car exclu de la décote (Article 11B NGAP).",
    /**
     * Taux à 0.01 car IFI applicable au 2ᵉ patient (ou plus) dépendant lors d'un même passage au même domicile.
     */
    T001_IFI: "Taux à 0.01 car IFI applicable au 2ᵉ patient (ou plus) dépendant lors d'un même passage au même domicile.",
    /**
     * Message pour acte associé à un pansement d'analgésie topique.
     * @param taux Le taux à afficher (en pourcentage, ex: 100, 50, 0)
     */
    T_ASSOC_ANALGESIE: function(taux: number): string {
        taux*=100;
        if (taux === 0) return this.T0;
        const rang = taux === 100 ? "1er" : "2nd";
        return `Taux à ${taux}% car ${rang} acte cumulé à taux plein avec l'analgésie topique.`;
    },
    /**
     * Message pour l'analgésie associée à un pansement d'ulcère ou d'escarre.
     * @param taux Le taux à afficher (en pourcentage, ex: 100, 50, 0)
     */
    T_ASSOC_PANSEMENT: function(taux: number): string {
        taux*=100;
        if (taux === 0) return this.T0;
        const rang = taux === 100 ? "1er" : "2nd";
        return `Taux à ${taux}% car ${rang} acte cumulé à taux plein avec un pansement d'ulcère ou d'escarre.`;
    },
    /**
     * Taux à X% car Xe acte cumulé à taux plein entre actes pour la prise en charge d'un patient insulino-traité.
     * @param taux Le taux appliqué (ex: 100, 50, 0)
     * @param rang Le rang de l'acte dans le cumul (ex: 1er, 2e, 3e...)
     */
    T_ASSOC_DIABETIQUE: function(taux: number): string {
        taux*=100;
        if (taux === 0) return this.T0;
        const rang = taux === 100 ? "1er" : "2nd";
        return `Taux à ${taux}% car ${rang} acte cumulé à taux plein entre actes pour la prise en charge d'un patient insulino-traité`;
    },
    /**
     * Message pour une perfusion courte associée à une perfusion longue et/ou un supplément.
     * @param taux Le taux à afficher (en pourcentage, ex: 100, 50, 0)
     */
    T_ASSOC_PERF_LONGUE_OU_SUPP: function(taux: number): string {
        taux *= 100;
        if (taux === 0) return this.T0;
        const rang = taux === 100 ? "1er" : "2nd";
        return `Taux à ${taux}% car ${rang} acte cumulé à taux plein avec une perfusion longue et/ou un ou plusieurs supplément.`;
    },
    /**
     * Message pour une perfusion longue ou un supplément associé à une perfusion courte.
     * @param taux Le taux à afficher (en pourcentage, ex: 100, 50, 0)
     */
    T_ASSOC_PERF_COURTE: function(taux: number): string {
        taux *= 100;
        if (taux === 0) return this.T0;
        const rang = taux === 100 ? "1er" : "2nd";
        return `Taux à ${taux}% car ${rang} acte cumulé à taux plein avec une perfusion courte.`;
    },
    /**
     * Message pour l'acte associé à une prise de sang (PDS).
     */
    T_ASSOC_PDS: "Taux à 100% car prise de sang cumulable à taux plein avec l'acte associé dans la limite de 2 actes (Article 11B NGAP)",
    /**
     * Message spécifique pour l'acte Prise de Sang (PDS) lorsqu'il est associé à un autre acte.
     */
    T_PDS: "Taux à 100% car cumulable à taux plein avec l'acte associé dans la limite de 2 actes (Article 11B NGAP)",
    /**
     * Message pour un acte postopératoire de surveillance (retrait de sonde ou surveillance de drain) associé à un autre acte à taux plein.
     * @param taux Le taux à afficher (en pourcentage, ex: 100, 50, 0)
     */
    T_ASSOC_POSTOP_SURVEILLANCE: function(taux: number): string {
        taux *= 100;
        if (taux === 0) return this.T0;
        const rang = taux === 100 ? "1er" : "2nd";
        return `Taux à ${taux}% car ${rang} acte cumulé à taux plein avec un retrait de sonde ou une surveillance de drain.`;
    },
    /**
     * Message pour un acte AMI 2 ou 2.8 (séance de surveillance postopératoire ou de cathéter périnerveux) associé à un autre acte à taux plein.
     * @param taux Le taux à afficher (en pourcentage, ex: 100, 50, 0)
     */
    T_ASSOC_POSTOP_AMI: function(taux: number): string {
        taux *= 100;
        if (taux === 0) return this.T0;
        const rang = taux === 100 ? "1er" : "2nd";
        return `Taux à ${taux}% car ${rang} acte cumulé à taux plein avec une séance de surveillance postopératoire ou de cathéter périnerveux.`;
    },
    /**
     * Retourne le message explicatif selon le taux (100, 50, 0).
     * @param taux Le taux à afficher (en pourcentage, ex: 100, 50, 0)
     */
    T_AUTO: function(taux: number): string {
        switch (taux) {
            case 100:
                return this.T100;
            case 50:
                return this.T50;
            case 0:
                return this.T0;
            default:
                return `Taux à ${taux}% automatiquement calculé par les règles métier.`;
        }
    },
};